import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Search, X, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { usersApi, rolesApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { usePermissions } from '@/contexts/PermissionContext'
import { isValidEmail, sanitizePhoneNumber } from '@/lib/utils'
import type { UserListItem, CreateUserRequest, UpdateUserRequest } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = '__all__'
const ALL_ROLES = '__all__'

interface UserFilters {
  searchTerm: string
  status: string
  role: string
}

export default function UsersPage() {
  const { t } = useTranslation()
  const { canAddEdit } = usePermissions()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<UserFilters>({
    searchTerm: '',
    status: ALL_STATUSES,
    role: ALL_ROLES,
  })
  const [formData, setFormData] = useState<CreateUserRequest>({
    full_name: '',
    user_login_id: '',
    user_email: '',
    phone_number: '',
    role_id: '',
  })

  const queryClient = useQueryClient()

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  const { data: roles = [] } = useQuery({
    queryKey: ['roles', 'active'],
    queryFn: () => rolesApi.getAll(true),
  })

  const { data: allRoles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.getAll(),
  })

  // Filter users based on filters
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesName = user.full_name.toLowerCase().includes(searchLower)
        const matchesEmail = user.user_email.toLowerCase().includes(searchLower)
        const matchesLoginId = user.user_login_id.toLowerCase().includes(searchLower)
        if (!matchesName && !matchesEmail && !matchesLoginId) return false
      }
      if (filters.status !== ALL_STATUSES) {
        const isActive = filters.status === 'active'
        if (user.is_active !== isActive) return false
      }
      if (filters.role !== ALL_ROLES) {
        if (user.role_id !== filters.role) return false
      }
      return true
    })
  }, [users, filters])

  // Pagination calculations
  const totalRecords = filteredUsers.length
  const totalPages = Math.ceil(totalRecords / pageSize)
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredUsers.slice(startIndex, startIndex + pageSize)
  }, [filteredUsers, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const clearFilters = () => {
    setFilters({ searchTerm: '', status: ALL_STATUSES, role: ALL_ROLES })
    setCurrentPage(1)
  }

  const hasActiveFilters = filters.searchTerm || filters.status !== ALL_STATUSES || filters.role !== ALL_ROLES

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['roles'] }) // Refresh role user counts
      setIsDialogOpen(false)
      resetForm()
      toast.success(t('users.userCreated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('users.failedToCreate')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['roles'] }) // Refresh role user counts
      setIsDialogOpen(false)
      setEditingUser(null)
      resetForm()
      toast.success(t('users.userUpdated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('users.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['roles'] }) // Refresh role user counts
      toast.success(t('users.userDeleted'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('users.failedToDelete')
      toast.error(errorMessage)
    },
  })

  const resetForm = () => {
    setFormData({
      full_name: '',
      user_login_id: '',
      user_email: '',
      phone_number: '',
      role_id: '',
    })
    setEditingUser(null)
  }

  const handleEdit = (user: UserListItem) => {
    setEditingUser(user)
    setFormData({
      full_name: user.full_name,
      user_login_id: user.user_login_id,
      user_email: user.user_email,
      phone_number: user.phone_number || '',
      role_id: user.role_id,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate email format
    if (!formData.user_email || !isValidEmail(formData.user_email)) {
      toast.error(t('users.invalidEmail', 'Please enter a valid email address'))
      return
    }

    if (editingUser) {
      updateMutation.mutate({
        id: editingUser.user_id,
        data: {
          full_name: formData.full_name,
          user_login_id: formData.user_login_id,
          user_email: formData.user_email,
          phone_number: formData.phone_number || undefined,
          role_id: formData.role_id,
        },
      })
    } else {
      createMutation.mutate({
        ...formData,
        phone_number: formData.phone_number || undefined,
      })
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) {
      resetForm()
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('users.title')}</h1>
          <p className="text-muted-foreground">
            {t('users.manageUsers')}
          </p>
        </div>

        {canAddEdit('USERS') && (
          <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {t('users.addUser')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingUser ? t('users.editUser') : t('users.addUser')}</DialogTitle>
                <DialogDescription>
                  {editingUser ? t('users.updateUserInfo') : t('users.createNewUser')}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="full_name">{t('users.fullName')} *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder={t('users.enterFullName')}
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="user_login_id">{t('users.userId')} *</Label>
                  <Input
                    id="user_login_id"
                    value={formData.user_login_id}
                    onChange={(e) => setFormData({ ...formData, user_login_id: e.target.value })}
                    placeholder={t('users.enterUserId')}
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="user_email">{t('common.email')} *</Label>
                  <Input
                    id="user_email"
                    type="email"
                    value={formData.user_email}
                    onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                    placeholder={t('users.enterEmail')}
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="phone_number">{t('common.phone')}</Label>
                  <Input
                    id="phone_number"
                    value={formData.phone_number || ''}
                    onChange={(e) => setFormData({ ...formData, phone_number: sanitizePhoneNumber(e.target.value) })}
                    placeholder={t('users.enterPhone')}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="role_id">{t('users.role')} *</Label>
                  <Select
                    value={formData.role_id}
                    onValueChange={(value) => setFormData({ ...formData, role_id: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('users.selectRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.role_id} value={role.role_id}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending || !formData.role_id}>
                  {createMutation.isPending || updateMutation.isPending
                    ? t('common.saving')
                    : editingUser
                    ? t('users.updateUser')
                    : t('users.createUser')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('users.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              setCurrentPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.role}
          onValueChange={(value) => {
            setFilters({ ...filters, role: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('users.allRoles')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ROLES}>{t('users.allRoles')}</SelectItem>
            {allRoles.map((role) => (
              <SelectItem key={role.role_id} value={role.role_id}>
                {role.role_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.status}
          onValueChange={(value) => {
            setFilters({ ...filters, status: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('common.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('common.allStatuses')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      <UsersTable
        users={paginatedUsers}
        allUsers={users}
        filteredUsers={filteredUsers}
        isLoading={usersLoading}
        onEdit={handleEdit}
        onDelete={(id) => deleteMutation.mutate(id)}
        isDeleting={deleteMutation.isPending}
        clearFilters={clearFilters}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        startRecord={startRecord}
        endRecord={endRecord}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
        t={t}
        canAddEdit={canAddEdit('USERS')}
      />
    </div>
  )
}

function UsersTable({
  users,
  allUsers,
  filteredUsers,
  isLoading,
  onEdit,
  onDelete,
  isDeleting,
  clearFilters,
  pageSize,
  currentPage,
  totalRecords,
  totalPages,
  startRecord,
  endRecord,
  onPageSizeChange,
  onNextPage,
  onPreviousPage,
  t,
  canAddEdit,
}: {
  users: UserListItem[]
  allUsers: UserListItem[]
  filteredUsers: UserListItem[]
  isLoading: boolean
  onEdit: (user: UserListItem) => void
  onDelete: (id: string) => void
  isDeleting: boolean
  clearFilters: () => void
  pageSize: number
  currentPage: number
  totalRecords: number
  totalPages: number
  startRecord: number
  endRecord: number
  onPageSizeChange: (size: string) => void
  onNextPage: () => void
  onPreviousPage: () => void
  t: (key: string) => string
  canAddEdit: boolean
}) {
  if (isLoading) {
    return <div className="text-center py-8">{t('common.loading')}</div>
  }

  if (allUsers.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('users.noUsersFound')}
        </CardContent>
      </Card>
    )
  }

  if (filteredUsers.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-muted-foreground mb-4">
            {t('users.noMatchingUsers')}
          </div>
          <Button variant="outline" onClick={clearFilters}>
            <X className="mr-2 h-4 w-4" />
            {t('common.reset')} {t('common.filters')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-280px)]">
      <Card className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>{t('users.fullName')}</TableHead>
                <TableHead>{t('users.userId')}</TableHead>
                <TableHead>{t('common.email')}</TableHead>
                <TableHead>{t('common.phone')}</TableHead>
                <TableHead>{t('users.role')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="w-[120px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell className="font-medium">{user.full_name}</TableCell>
                  <TableCell>{user.user_login_id}</TableCell>
                  <TableCell>{user.user_email}</TableCell>
                  <TableCell>{user.phone_number || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{user.role_name}</Badge>
                  </TableCell>
                  <TableCell>
                    {user.is_active ? (
                      <Badge variant="default">{t('common.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('common.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canAddEdit && (
                          <>
                            <DropdownMenuItem onClick={() => onEdit(user)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onDelete(user.user_id)}
                              disabled={isDeleting}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('common.delete')}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center gap-3 px-4 py-2 border-t">
          <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
            <SelectTrigger className="w-[70px] h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {startRecord}-{endRecord} {t('common.of')} {totalRecords}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onPreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onNextPage}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

